/**
 * Canais Removidos - Sistema de Canais Excluidos do YouTube
 * =========================================================
 *
 * Exibe canais que foram banidos/removidos do YouTube com todas as informacoes
 * que tinham antes da exclusao.
 *
 * Fontes de dados:
 * - Automatica: Canais detectados como removidos na atualizacao via API
 * - Manual: Upload de JSON de canais terminados (ex: Nexlev)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  AlertTriangle, Search, Upload,
  Loader, X, FileJson, Calendar, Eye, Users, Video, DollarSign,
  ChevronLeft, ChevronRight
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useSidebar } from '../contexts/SidebarContext';
import { usePrivacy } from '../contexts/PrivacyContext';
import { usePageTitle } from '../hooks/usePageTitle';
import Sidebar from './Sidebar';
import Header from './Header';
import PageHeader from './ui/PageHeader';

const API = import.meta.env.VITE_BACKEND_URL + '/api';

// Helper to format numbers
const formatNumber = (num) => {
  if (!num) return '0';
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};

// Helper to format date
const formatDate = (dateStr) => {
  if (!dateStr) return null; // handled by caller with t()
  const date = new Date(dateStr);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

// Helper to ensure URL has protocol
const ensureHttps = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `https://${url}`;
};

// Get channel thumbnail
const getChannelThumbnail = (channel) => {
  if (channel.thumbnail && channel.thumbnail.length > 10) {
    return channel.thumbnail;
  }
  if (channel.channelAvatar && channel.channelAvatar.length > 10) {
    return channel.channelAvatar;
  }
  const initial = (channel.title || channel.name || 'C')[0].toUpperCase();
  return `https://ui-avatars.com/api/?name=${initial}&background=6B7280&color=fff&size=176&bold=true`;
};

const CanaisRemovidosPage = ({ onLogout, isSuperAdmin }) => {
  const { t } = useTranslation();
  usePageTitle(t('removedChannels.title'));
  const { sidebarWidth, isMobile } = useSidebar();
  const { isPrivate } = usePrivacy();

  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [stats, setStats] = useState({ total: 0 });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalItems, setTotalItems] = useState(0);

  // Fetch removed channels
  const fetchChannels = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const offset = (currentPage - 1) * itemsPerPage;
      const response = await axios.get(`${API}/canais-removidos`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          limit: itemsPerPage,
          offset: offset,
          search: searchTerm || undefined
        }
      });
      setChannels(response.data.channels || []);
      setTotalItems(response.data.total || 0);
      setStats({ total: response.data.total || 0 });
    } catch (error) {
      console.error('Error fetching removed channels:', error);
      toast.error(t('removedChannels.error_load'));
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, searchTerm]);

  useEffect(() => {
    fetchChannels();
  }, [fetchChannels]);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Calculate pagination
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      toast.error(t('removedChannels.select_json'));
      return;
    }

    setUploading(true);
    setShowUploadModal(false);

    const toastId = toast.loading(t('removedChannels.importing'), {
      description: t('removedChannels.importing_desc')
    });

    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/canais-removidos/import`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.dismiss(toastId);

      if (response.data.success) {
        toast.success(response.data.message, {
          description: t('removedChannels.channels_imported', { count: response.data.stats.imported })
        });
        fetchChannels();
      }
    } catch (error) {
      toast.dismiss(toastId);
      console.error('Error uploading file:', error);
      toast.error(error.response?.data?.detail || t('removedChannels.error_import'));
    } finally {
      setUploading(false);
    }
  };

  // Sincronizar canais removidos de Nichos Virais
  const handleSyncFromNichos = async () => {
    setUploading(true);
    const toastId = toast.loading(t('removedChannels.syncing_nichos'));

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/canais-removidos/sync-from-nichos`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.dismiss(toastId);

      if (response.data.success) {
        toast.success(response.data.message, {
          description: t('removedChannels.channels_migrated', { count: response.data.stats.migrated })
        });
        fetchChannels();
      }
    } catch (error) {
      toast.dismiss(toastId);
      console.error('Error syncing from nichos:', error);
      toast.error(error.response?.data?.detail || t('removedChannels.error_sync'));
    } finally {
      setUploading(false);
    }
  };

  // Atualizar imagens de canais sem foto
  const handleUpdateMissingImages = async () => {
    setUploading(true);
    const toastId = toast.loading(t('removedChannels.updating_images'));

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/canais-removidos/update-missing-images`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.dismiss(toastId);

      if (response.data.success) {
        toast.success(response.data.message, {
          description: t('removedChannels.images_updated', { count: response.data.stats.updated })
        });
        fetchChannels();
      }
    } catch (error) {
      toast.dismiss(toastId);
      console.error('Error updating images:', error);
      toast.error(error.response?.data?.detail || t('removedChannels.error_update_images'));
    } finally {
      setUploading(false);
    }
  };

  // Note: Filter is now done server-side via searchTerm in fetchChannels
  const filteredChannels = channels;

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--dp-bg-primary)' }}>
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .animate-spin { animation: spin 1s linear infinite; }
      `}</style>

      <Sidebar onLogout={onLogout} isSuperAdmin={isSuperAdmin} />
      <Header />

      <main className="dp-main-content" style={{
        marginLeft: isMobile ? 0 : `${sidebarWidth}px`,
        marginTop: '60px',
        flex: 1,
        minWidth: 0,
        transition: 'margin-left 0.3s ease',
        animation: 'dp-pageFadeIn 0.35s ease',
      }}>
        {/* Header */}
        <PageHeader
          icon={<AlertTriangle size={20} style={{ color: 'var(--dp-blue-500, #0DAEFF)' }} />}
          title={t('removedChannels.title')}
          description={t('removedChannels.subtitle')}
          badge={`${stats.total} ${t('removedChannels.total_removed')}`}
        >
          {/* Admin Action Buttons */}
          {isSuperAdmin && (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button
                className="dp-btn-cta dp-btn-cta--sm"
                onClick={() => setShowUploadModal(true)}
                disabled={uploading}
                data-testid="import-json-btn"
              >
                {uploading ? <Loader size={14} className="animate-spin" /> : <Upload size={14} />}
                {uploading ? t('removedChannels.processing') : t('removedChannels.import')}
              </button>

              <button
                className="dp-btn-cta dp-btn-cta--sm"
                onClick={handleSyncFromNichos}
                disabled={uploading}
                data-testid="sync-nichos-btn"
                title={t('removedChannels.sync_tooltip')}
              >
                <Users size={14} />
                {t('removedChannels.sync')}
              </button>

              <button
                className="dp-btn-cta dp-btn-cta--sm"
                onClick={handleUpdateMissingImages}
                disabled={uploading}
                data-testid="update-images-btn"
                title={t('removedChannels.update_images_tooltip')}
              >
                <Eye size={14} />
                {t('removedChannels.update_images')}
              </button>
            </div>
          )}
        </PageHeader>

        {/* Warning Banner */}
        <div style={{
          marginBottom: '20px',
          background: 'rgba(239, 68, 68, 0.08)',
          border: '1px solid rgba(239, 68, 68, 0.2)',
          borderRadius: '12px',
          padding: '16px 20px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <AlertTriangle size={20} style={{ color: '#EF4444', flexShrink: 0 }} />
          <p style={{
            color: 'var(--dp-text-primary)',
            fontSize: '14px',
            fontFamily: 'var(--dp-font)',
            margin: 0,
            lineHeight: '1.5'
          }}>
            <strong>{t('removedChannels.warning_title')}:</strong> {t('removedChannels.warning_desc')}
          </p>
        </div>

        {/* Search Bar */}
        <div style={{
          marginBottom: '20px',
          position: 'relative'
        }}>
          <Search size={16} style={{
            position: 'absolute',
            left: '14px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--dp-text-secondary)'
          }} />
          <input
            type="text"
            placeholder={t('removedChannels.search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="search-input"
            style={{
              width: '100%',
              padding: '14px 14px 14px 44px',
              background: 'var(--dp-bg-input, #2E2E2E)',
              color: 'var(--dp-text-primary)',
              border: '1px solid var(--dp-border-subtle)',
              borderRadius: '12px',
              fontSize: '14px',
              fontFamily: 'var(--dp-font)',
              outline: 'none'
            }}
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              style={{
                position: 'absolute',
                right: '14px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                color: 'var(--dp-text-secondary)',
                cursor: 'pointer'
              }}
            >
              <X size={18} />
            </button>
          )}
        </div>

        {/* Content */}
        {loading ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '60px',
            background: 'var(--dp-bg-card)',
            borderRadius: '16px',
            border: '1px solid var(--dp-border-subtle)'
          }}>
            <Loader size={40} className="animate-spin" style={{ color: 'var(--dp-blue-500, #0DAEFF)' }} />
          </div>
        ) : filteredChannels.length === 0 ? (
          <EmptyState
            isSuperAdmin={isSuperAdmin}
            onUpload={() => setShowUploadModal(true)}
            t={t}
          />
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '16px'
          }}>
            {filteredChannels.map(channel => (
              <RemovedChannelCard
                key={channel.id}
                channel={channel}
                isPrivate={isPrivate}
                t={t}
              />
            ))}
          </div>
        )}

        {/* Pagination Controls */}
        {totalItems > 0 && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
            marginTop: '32px',
            paddingTop: '24px',
            borderTop: '1px solid var(--dp-border-subtle)'
          }}>
            {/* Items info */}
            <p style={{
              color: 'var(--dp-text-secondary)',
              fontSize: '14px',
              fontFamily: 'var(--dp-font)',
              margin: 0
            }}>
              {t('removedChannels.showing_range', { start: startItem, end: endItem, total: totalItems })}
            </p>

            {/* Pagination row */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              flexWrap: 'wrap',
              justifyContent: 'center'
            }}>
              {/* Items per page selector */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ color: 'var(--dp-text-secondary)', fontSize: '14px', fontFamily: 'var(--dp-font)' }}>{t('removedChannels.items_per_page')}:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  style={{
                    padding: '8px 12px',
                    background: 'var(--dp-bg-card)',
                    color: 'var(--dp-text-primary)',
                    border: '1px solid var(--dp-border-subtle)',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontFamily: 'var(--dp-font)'
                  }}
                >
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>

              {/* Page navigation */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  style={{
                    padding: '8px 12px',
                    background: currentPage === 1 ? 'var(--dp-border-subtle)' : 'var(--dp-bg-card)',
                    color: currentPage === 1 ? 'var(--dp-text-secondary)' : 'var(--dp-text-primary)',
                    border: '1px solid var(--dp-border-subtle)',
                    borderRadius: '8px',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontFamily: 'var(--dp-font)',
                    opacity: currentPage === 1 ? 0.5 : 1
                  }}
                >
                  {t('removedChannels.first')}
                </button>

                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  style={{
                    padding: '8px 12px',
                    background: currentPage === 1 ? 'var(--dp-border-subtle)' : 'var(--dp-bg-card)',
                    color: currentPage === 1 ? 'var(--dp-text-secondary)' : 'var(--dp-text-primary)',
                    border: '1px solid var(--dp-border-subtle)',
                    borderRadius: '8px',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontFamily: 'var(--dp-font)',
                    opacity: currentPage === 1 ? 0.5 : 1
                  }}
                >
                  <ChevronLeft size={16} />
                  {t('removedChannels.previous')}
                </button>

                <span style={{
                  padding: '8px 16px',
                  background: 'var(--dp-blue-500, #0DAEFF)',
                  color: 'white',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  fontFamily: 'var(--dp-font)'
                }}>
                  {currentPage} / {totalPages}
                </span>

                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: '8px 12px',
                    background: currentPage === totalPages ? 'var(--dp-border-subtle)' : 'var(--dp-bg-card)',
                    color: currentPage === totalPages ? 'var(--dp-text-secondary)' : 'var(--dp-text-primary)',
                    border: '1px solid var(--dp-border-subtle)',
                    borderRadius: '8px',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontFamily: 'var(--dp-font)',
                    opacity: currentPage === totalPages ? 0.5 : 1
                  }}
                >
                  {t('removedChannels.next')}
                  <ChevronRight size={16} />
                </button>

                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: '8px 12px',
                    background: currentPage === totalPages ? 'var(--dp-border-subtle)' : 'var(--dp-bg-card)',
                    color: currentPage === totalPages ? 'var(--dp-text-secondary)' : 'var(--dp-text-primary)',
                    border: '1px solid var(--dp-border-subtle)',
                    borderRadius: '8px',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontFamily: 'var(--dp-font)',
                    opacity: currentPage === totalPages ? 0.5 : 1
                  }}
                >
                  {t('removedChannels.last')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Upload Modal */}
        {showUploadModal && (
          <UploadModal
            uploading={uploading}
            onUpload={handleFileUpload}
            onClose={() => setShowUploadModal(false)}
            t={t}
          />
        )}
      </main>
    </div>
  );
};

// Removed Channel Card Component - Compact with Video Carousel
const RemovedChannelCard = ({ channel, isPrivate, t }) => {
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  // Get top 3 videos
  const topVideos = (channel.topVideos || channel.top_videos || []).slice(0, 3);
  const hasMultipleVideos = topVideos.length > 1;

  const nextVideo = (e) => {
    e.stopPropagation();
    setCurrentVideoIndex((prev) => (prev + 1) % topVideos.length);
  };

  const prevVideo = (e) => {
    e.stopPropagation();
    setCurrentVideoIndex((prev) => (prev - 1 + topVideos.length) % topVideos.length);
  };

  return (
    <div
      data-testid={`channel-card-${channel.id}`}
      className="dp-top-accent"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        background: 'var(--dp-bg-card)',
        border: `1px solid ${isHovered ? 'rgba(13, 174, 255, 0.25)' : 'var(--dp-border-subtle)'}`,
        borderRadius: '14px',
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
        boxShadow: isHovered
          ? '0 12px 24px -8px rgba(0,0,0,0.2), 0 4px 8px -4px rgba(0,0,0,0.1)'
          : '0 2px 8px rgba(0,0,0,0.05)'
      }}
    >
      {/* Header - Compact */}
      <div style={{
        padding: '12px',
        display: 'flex',
        gap: '10px',
        alignItems: 'center'
      }}>
        <img
          src={isPrivate ? `https://ui-avatars.com/api/?name=?&background=888888&size=176&bold=true` : getChannelThumbnail(channel)}
          alt={isPrivate ? 'Canal' : channel.title}
          onError={(e) => {
            e.target.src = `https://ui-avatars.com/api/?name=${isPrivate ? '?' : (channel.title || 'C')[0]}&background=6B7280&color=fff&size=176&bold=true`;
          }}
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            objectFit: 'cover',
            border: '2px solid #6B728030',
            filter: 'grayscale(100%)',
            opacity: 0.85,
            transition: 'all 0.3s ease',
            transform: isHovered ? 'scale(1.05)' : 'scale(1)'
          }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <a
              href={channel.url ? ensureHttps(channel.url) : '#'}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => !channel.url && e.preventDefault()}
              style={{
                fontSize: '13px',
                fontWeight: '600',
                fontFamily: 'var(--dp-font)',
                color: 'var(--dp-text-primary)',
                margin: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
                textDecoration: 'none',
                cursor: channel.url ? 'pointer' : 'default',
                transition: 'color 0.2s ease'
              }}
              onMouseEnter={(e) => channel.url && (e.target.style.color = 'var(--dp-blue-500, #0DAEFF)')}
              onMouseLeave={(e) => e.target.style.color = 'var(--dp-text-primary)'}
            >
              {isPrivate ? '••••••••••' : (channel.title || t('removedChannels.no_name'))}
            </a>
            <span style={{
              fontSize: '8px',
              padding: '2px 6px',
              background: '#EF444420',
              color: '#EF4444',
              borderRadius: '10px',
              fontWeight: '700',
              fontFamily: 'var(--dp-font)',
              letterSpacing: '0.5px'
            }}>
              {t('removedChannels.removed_badge')}
            </span>
          </div>

          {/* Date + Monetized Badge */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
            {channel.terminatedAt && (
              <span style={{
                fontSize: '10px',
                color: 'var(--dp-text-secondary)',
                fontFamily: 'var(--dp-font)',
                display: 'flex',
                alignItems: 'center',
                gap: '3px'
              }}>
                <Calendar size={10} />
                {formatDate(channel.terminatedAt) || t('removedChannels.unknown_date')}
              </span>
            )}
            {channel.isMonetized && (
              <span style={{
                fontSize: '9px',
                padding: '2px 5px',
                background: '#10B98115',
                color: '#10B981',
                borderRadius: '8px',
                fontWeight: '600',
                fontFamily: 'var(--dp-font)',
                display: 'flex',
                alignItems: 'center',
                gap: '2px'
              }}>
                <DollarSign size={8} />
                $
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Video Carousel */}
      {topVideos.length > 0 && (
        <div style={{
          position: 'relative',
          margin: '0 12px',
          borderRadius: '10px',
          overflow: 'hidden'
        }}>
          {/* Current Video Thumbnail */}
          <div style={{
            position: 'relative',
            width: '100%',
            height: '140px',
            background: '#1a1a1a'
          }}>
            <img
              src={isPrivate ? 'https://placehold.co/320x180/888888/ffffff?text=?' : topVideos[currentVideoIndex]?.video_thumbnail_url}
              alt={isPrivate ? 'Video' : topVideos[currentVideoIndex]?.video_title}
              onError={(e) => {
                e.target.src = 'https://placehold.co/320x180/374151/9CA3AF?text=Video';
              }}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                filter: 'grayscale(80%)',
                opacity: 0.9,
                transition: 'all 0.4s ease'
              }}
            />

            {/* Video Duration - Bottom Left */}
            {topVideos[currentVideoIndex]?.length_text && (
              <span style={{
                position: 'absolute',
                bottom: '6px',
                left: '6px',
                background: 'rgba(0,0,0,0.85)',
                color: 'white',
                padding: '2px 5px',
                borderRadius: '4px',
                fontSize: '10px',
                fontWeight: '600',
                fontFamily: 'monospace'
              }}>
                {topVideos[currentVideoIndex].length_text}
              </span>
            )}

            {/* Video Views Badge */}
            <span style={{
              position: 'absolute',
              bottom: '6px',
              right: '6px',
              background: 'rgba(0,0,0,0.85)',
              color: 'white',
              padding: '2px 5px',
              borderRadius: '4px',
              fontSize: '9px',
              fontWeight: '500',
              fontFamily: 'var(--dp-font)',
              display: 'flex',
              alignItems: 'center',
              gap: '3px'
            }}>
              <Eye size={10} />
              {isPrivate ? '•••' : formatNumber(topVideos[currentVideoIndex]?.video_view_count)}
            </span>

            {/* Navigation Arrows */}
            {hasMultipleVideos && (
              <>
                <button
                  onClick={prevVideo}
                  style={{
                    position: 'absolute',
                    left: '6px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    background: 'rgba(0,0,0,0.7)',
                    border: 'none',
                    color: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: isHovered ? 1 : 0,
                    transition: 'all 0.3s ease',
                    backdropFilter: 'blur(4px)'
                  }}
                >
                  <ChevronLeft size={16} />
                </button>
                <button
                  onClick={nextVideo}
                  style={{
                    position: 'absolute',
                    right: '6px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    background: 'rgba(0,0,0,0.7)',
                    border: 'none',
                    color: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: isHovered ? 1 : 0,
                    transition: 'all 0.3s ease',
                    backdropFilter: 'blur(4px)'
                  }}
                >
                  <ChevronRight size={16} />
                </button>
              </>
            )}

            {/* Video Indicators */}
            {hasMultipleVideos && (
              <div style={{
                position: 'absolute',
                top: '6px',
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                gap: '4px'
              }}>
                {topVideos.map((_, idx) => (
                  <div
                    key={idx}
                    style={{
                      width: idx === currentVideoIndex ? '16px' : '6px',
                      height: '6px',
                      borderRadius: '3px',
                      background: idx === currentVideoIndex ? 'white' : 'rgba(255,255,255,0.4)',
                      transition: 'all 0.3s ease'
                    }}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Video Title */}
          <div style={{
            padding: '8px',
            background: 'rgba(0,0,0,0.15)'
          }}>
            <p style={{
              fontSize: '11px',
              color: 'var(--dp-text-primary)',
              fontFamily: 'var(--dp-font)',
              margin: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontWeight: '500'
            }}>
              {isPrivate ? '•••••••••••••' : topVideos[currentVideoIndex]?.video_title}
            </p>
          </div>
        </div>
      )}

      {/* Stats Row - Compact */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-around',
        padding: '10px 12px',
        borderTop: '1px solid var(--dp-border-subtle)',
        marginTop: '12px'
      }}>
        <MiniStat icon={<Eye size={12} />} value={isPrivate ? '•••' : formatNumber(channel.avgViewPerVideo)} label={t('removedChannels.views')} />
        <MiniStat icon={<Video size={12} />} value={isPrivate ? '•••' : channel.numOfUploads} label={t('removedChannels.videos')} />
        <MiniStat icon={<Users size={12} />} value={isPrivate ? '•••' : formatNumber(channel.subscribers)} label={t('removedChannels.subscribers')} />
      </div>

      {/* Categories */}
      {(channel.categories?.length > 0) && (
        <div style={{
          padding: '0 12px 10px',
          display: 'flex',
          gap: '4px',
          flexWrap: 'wrap'
        }}>
          {(channel.categories || []).slice(0, 2).map((cat, idx) => (
            <span key={idx} style={{
              fontSize: '9px',
              padding: '2px 6px',
              background: 'var(--dp-bg-input, #2E2E2E)',
              color: 'var(--dp-text-secondary)',
              borderRadius: '6px',
              fontWeight: '500',
              fontFamily: 'var(--dp-font)',
              textTransform: 'capitalize'
            }}>
              {isPrivate ? '•••••' : cat}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// Mini Stat Component
const MiniStat = ({ icon, value, label }) => (
  <div style={{ textAlign: 'center' }}>
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '4px',
      color: 'var(--dp-text-primary)',
      fontWeight: '600',
      fontSize: '13px',
      fontFamily: 'var(--dp-font)'
    }}>
      <span style={{ color: 'var(--dp-text-secondary)' }}>{icon}</span>
      {value}
    </div>
    <p style={{ fontSize: '9px', color: 'var(--dp-text-secondary)', fontFamily: 'var(--dp-font)', margin: '2px 0 0' }}>{label}</p>
  </div>
);

// Empty State Component
const EmptyState = ({ isSuperAdmin, onUpload, t }) => (
  <div style={{
    textAlign: 'center',
    padding: '60px 20px',
    background: 'var(--dp-bg-card)',
    borderRadius: '16px',
    border: '1px solid var(--dp-border-subtle)'
  }}>
    <div style={{
      width: '80px',
      height: '80px',
      margin: '0 auto 24px',
      background: 'linear-gradient(135deg, #6B7280, #4B5563)',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <AlertTriangle size={40} style={{ color: 'white' }} />
    </div>
    <h3 style={{
      fontSize: '20px',
      fontWeight: '600',
      color: 'var(--dp-text-primary)',
      fontFamily: 'var(--dp-font)',
      margin: '0 0 12px 0'
    }}>
      {t('removedChannels.no_channels')}
    </h3>
    <p style={{
      fontSize: '14px',
      color: 'var(--dp-text-secondary)',
      fontFamily: 'var(--dp-font)',
      margin: '0 0 24px 0',
      maxWidth: '400px',
      marginLeft: 'auto',
      marginRight: 'auto'
    }}>
      {t('removedChannels.no_channels_desc')}
    </p>
    {isSuperAdmin && (
      <button
        className="dp-btn-cta dp-btn-cta--sm"
        onClick={onUpload}
        data-testid="upload-json-empty-btn"
      >
        <FileJson size={14} />
        {t('removedChannels.import')}
      </button>
    )}
  </div>
);

// Upload Modal Component
const UploadModal = ({ uploading, onUpload, onClose, t }) => (
  <div className="dp-modal-overlay" style={{
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0, 0, 0, 0.6)',
    backdropFilter: 'blur(8px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '16px',
    animation: 'dp-modal-overlay 0.2s ease',
  }} onClick={onClose}>
    <div style={{
      background: 'var(--dp-bg-glass)',
      backdropFilter: 'blur(24px)',
      WebkitBackdropFilter: 'blur(24px)',
      borderRadius: '16px',
      padding: '28px',
      maxWidth: '500px',
      width: '100%',
      maxHeight: '90vh',
      overflowY: 'auto',
      border: '1px solid var(--dp-border-medium)',
      boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
      animation: 'dp-modal-scale 0.25s cubic-bezier(0.4,0,0.2,1)',
    }} onClick={e => e.stopPropagation()}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 600, color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)', margin: 0 }}>
          {t('removedChannels.import')}
        </h2>
        <button
          onClick={onClose}
          style={{
            width: '32px', height: '32px', borderRadius: '8px',
            border: '1px solid var(--dp-border-subtle)', background: 'transparent',
            color: 'var(--dp-text-secondary)', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s',
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = '#F87171'; e.currentTarget.style.color = '#F87171'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.color = 'var(--dp-text-secondary)'; }}
        >
          <X size={16} />
        </button>
      </div>

      <div style={{
        background: 'var(--dp-bg-input)',
        border: '2px dashed var(--dp-border-medium)',
        borderRadius: '14px',
        padding: '36px',
        textAlign: 'center',
        marginBottom: '20px'
      }}>
        <FileJson size={48} style={{ color: 'var(--dp-text-tertiary)', marginBottom: '16px' }} />
        <p style={{ color: 'var(--dp-text-primary)', fontSize: '16px', fontWeight: '600', fontFamily: 'var(--dp-font)', margin: '0 0 8px 0' }}>
          {t('removedChannels.select_json_file')}
        </p>
        <p style={{ color: 'var(--dp-text-secondary)', fontSize: '13px', fontFamily: 'var(--dp-font)', margin: '0 0 20px 0' }}>
          {t('removedChannels.json_file_desc')}
        </p>
        <label style={{
          padding: '12px 24px',
          background: uploading ? 'var(--dp-border-subtle)' : 'linear-gradient(135deg, var(--dp-blue-500), var(--dp-blue-600))',
          color: 'white',
          borderRadius: '10px',
          fontSize: '14px',
          fontWeight: '600',
          fontFamily: 'var(--dp-font)',
          cursor: uploading ? 'not-allowed' : 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          {uploading ? (
            <>
              <Loader size={18} className="animate-spin" />
              {t('removedChannels.processing')}
            </>
          ) : (
            <>
              <Upload size={18} />
              {t('removedChannels.select_file')}
            </>
          )}
          <input
            type="file"
            accept=".json"
            onChange={onUpload}
            disabled={uploading}
            style={{ display: 'none' }}
            data-testid="file-input"
          />
        </label>
      </div>

      <div style={{
        background: 'var(--dp-blue-subtle)',
        borderRadius: '10px',
        padding: '14px'
      }}>
        <p style={{ fontSize: '12px', color: 'var(--dp-text-secondary)', fontFamily: 'var(--dp-font)', margin: 0 }}>
          <strong>{t('removedChannels.expected_format')}</strong> {t('removedChannels.expected_format_desc')}
        </p>
      </div>
    </div>
  </div>
);

export default CanaisRemovidosPage;
